import React from 'react';
import type * as Immutable from 'immutable';
import { Button, Input, LoadingIndicator } from '@milanote/roma';
import { AssetPicker } from '@milanote/roma/application';
import { SearchMdIcon } from '@milanote/roma-icons';

import type { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import ElementIcon from '../../../../../../components/elementIcon/ElementIcon';
import { useTranslator } from '../../../../../../components/i18n/useTranslator';
import type { IconPickerTab } from './iconPickerTypes';
import useIconPicker from './useIconPicker';

type IconPickerProps = {
    selectedElements: Immutable.List<ImMNElement>;
};

// `ElementIcon` sets its own size and colour in unlayered SCSS, which beats Tailwind's layers unless important.
const TILE_CLASS_NAME =
    'bg-bg-secondary_alpha_alt [&_.ElementIcon]:[--element-icon-color:currentColor]! [&_.ElementIcon]:[--element-icon-size:2.8rem]!';

const PANE_MESSAGE_CLASS_NAME =
    'flex h-full flex-col items-center justify-center gap-md text-center text-sm text-tertiary';

/**
 * Roma board icon picker: category and search tabs beside a grid of icons for the selected tab.
 */
const IconPicker: React.FC<IconPickerProps> = ({ selectedElements }) => {
    const translate = useTranslator('popup', 'icon');
    const {
        tabs,
        selectedTab,
        selectTab,
        searchText,
        setSearchText,
        onSearchSubmit,
        status,
        icons,
        retry,
        showRecommended,
        selectedIconKeys,
        onIconSelectionChange,
    } = useIconPicker(selectedElements);

    const searchPlaceholder = translate('searchPlaceholder');

    /**
     * Search tabs show their query as typed into search, lowercased.
     */
    const getTabLabel = (tab: IconPickerTab) => {
        if (tab.type === 'recommended') return translate('categories.recommended');
        if (tab.type === 'letters') return translate('categories.lettersAndNumbers');

        return tab.query;
    };

    const errorMessage = (
        <p className="flex items-center justify-center gap-md text-sm text-tertiary">
            {translate('error.message')}
            <Button color="link-color" size="sm" onClick={retry}>
                {translate('error.retry')}
            </Button>
        </p>
    );

    const renderPaneContent = () => {
        if (status === 'loading') {
            return (
                <div className="flex h-full items-center justify-center">
                    <LoadingIndicator />
                </div>
            );
        }

        if (status === 'error' && !icons.length) return <div className={PANE_MESSAGE_CLASS_NAME}>{errorMessage}</div>;

        if (!icons.length) {
            const query = selectedTab.type === 'search' ? selectedTab.query : '';

            return (
                <div className={PANE_MESSAGE_CLASS_NAME}>
                    <p>{translate('noResults.message', { query })}</p>
                    <Button color="link-color" size="sm" onClick={showRecommended}>
                        {translate('noResults.link')}
                    </Button>
                </div>
            );
        }

        return (
            <div className="flex flex-col gap-xl">
                {status === 'error' && errorMessage}
                <AssetPicker.Grid
                    aria-label={translate('iconsLabel')}
                    items={icons}
                    selectionMode="single"
                    disallowEmptySelection
                    selectedKeys={selectedIconKeys}
                    onSelectionChange={onIconSelectionChange}
                >
                    {(icon) => (
                        <AssetPicker.Tile
                            id={icon.id}
                            textValue={'name' in icon ? icon.name : icon.string}
                            size="xl"
                            className={TILE_CLASS_NAME}
                        >
                            <ElementIcon icon={icon} />
                        </AssetPicker.Tile>
                    )}
                </AssetPicker.Grid>
            </div>
        );
    };

    return (
        <AssetPicker.Tabs selectedKey={selectedTab.id} onSelectionChange={selectTab}>
            <AssetPicker>
                <AssetPicker.Sidebar>
                    <AssetPicker.TabList aria-label={translate('categoriesLabel')} items={tabs}>
                        {(tab) => <AssetPicker.Tab id={tab.id}>{getTabLabel(tab)}</AssetPicker.Tab>}
                    </AssetPicker.TabList>
                    <AssetPicker.SidebarFooter>
                        <form onSubmit={onSearchSubmit}>
                            <Input
                                size="sm"
                                autoFocus
                                aria-label={searchPlaceholder}
                                placeholder={`${searchPlaceholder}...`}
                                iconTrailing={SearchMdIcon}
                                value={searchText}
                                onChange={setSearchText}
                            />
                        </form>
                    </AssetPicker.SidebarFooter>
                </AssetPicker.Sidebar>
                <AssetPicker.TabPanel id={selectedTab.id}>{renderPaneContent()}</AssetPicker.TabPanel>
            </AssetPicker>
        </AssetPicker.Tabs>
    );
};

export default IconPicker;
